import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';

/**
 * Dynamic CTA for a membership plan — behavior driven entirely by plan.action_type.
 *
 *  action_type === "external_link"       → <a target="_blank"> to plan.action_url (Zeffy, PayPal, etc.)
 *  action_type === "custom_internal_page" → <Link> to plan.action_url (must start with /)
 *  action_type === "portal_flow" or null/missing → default flow → /member-login
 *
 * Props:
 *   plan       - the plan object
 *   className  - additional classes for the <Button>
 *   style      - inline style for the <Button>
 *   defaultLabel - label to show when plan.action_label is not set (default: "Apply for Membership")
 *   testIdIndex  - suffix for the data-testid
 */
export const PlanActionButton = ({
  plan,
  className = 'w-full h-11 rounded-lg text-[13px] font-semibold',
  style = {},
  defaultLabel = 'Apply for Membership',
  testIdIndex = 0,
}) => {
  const action_type = plan?.action_type || 'portal_flow';
  const action_url = (plan?.action_url || '').trim();
  const label = plan?.action_label || defaultLabel;

  // External payment link (Zeffy, PayPal, Square, etc.) — open in new tab
  if (action_type === 'external_link' && action_url) {
    return (
      <a
        href={action_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`plan-action-external-${testIdIndex}`}
        className="block"
      >
        <Button className={className} style={style}>
          {label} <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </a>
    );
  }

  // Custom internal route (e.g. /membership/apply)
  if (action_type === 'custom_internal_page' && action_url.startsWith('/')) {
    return (
      <Link to={action_url} data-testid={`plan-action-internal-${testIdIndex}`}>
        <Button className={className} style={style}>
          {label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </Link>
    );
  }

  // Default — portal flow (unchanged behavior)
  return (
    <Link to="/member-login" data-testid={`plan-action-portal-${testIdIndex}`}>
      <Button className={className} style={style}>
        {label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
      </Button>
    </Link>
  );
};

export default PlanActionButton;
